import React, { useRef } from "react";
import Form from "react-bootstrap/Form";
import { Formik, FormikProps } from "formik";
import * as Yup from "yup";
import { Label } from "reactstrap";
import { Editor } from "react-draft-wysiwyg";
import { ContentState, convertFromHTML, convertToRaw, EditorState } from "draft-js";
import draftToHtml from 'draftjs-to-html';

//Common
import { FormBackGround } from "../../common/Forms";
import FormHeader from "../../common/Forms/FormHeader/FormHeader";

//App
import { EMPTY_GUID } from "../../../utility/constants";
import { TemplateFormProps } from "../../../types/template";
import { Chip, TextField } from "../../common/VFX-UI";
import ActionBar from "../../common/ActionBar/ActionBar";
import useComponentGeneralStyles from "../../styles";
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import EditButton from "../../common/Buttons/EditButton";
import UpdateDialog from "../../Employee/Pledge/UpdateDialog";

const timeRegex = /^\d{2}:\d{2}:\d{2}$/;

const validationSchema = Yup.object().shape({
  maxProcessTime: Yup.string().matches(timeRegex, "Time needs to be in HH:MM:SS")
    .nullable().test({
      name: "Is max time greater",
      exclusive: false,
      params: {},
      message: "Max process time must be greater than min process time",
      test: function (value) {
        if (!value) return true;
        return value >= this.parent.minProcessTime;
      },
    }),
  minProcessTime:  Yup.string().matches(timeRegex, "Time needs to be in HH:MM:SS")
    .nullable(),
});

const TemplateEmailForm = (props: TemplateFormProps) => {
  const { status, initialVal, onClose, handleOnSubmit } = props;
  const formRef = useRef<FormikProps<any>>(null);
  const generalStyles = useComponentGeneralStyles();
  const [isEditModalOpen, setIsEditModalOpen] = React.useState<boolean>(false);

  const { id } = initialVal;
  const title = id === EMPTY_GUID ? "New Template" : "Edit Template";

  const saveInput = () => {
    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  };
  const html = initialVal?.htmlContent;
  const blocksFromHTML = convertFromHTML(html);
  const [editorState, setEditorState] = React.useState(() =>EditorState.createWithContent
    (ContentState.createFromBlockArray(blocksFromHTML.contentBlocks, blocksFromHTML.entityMap)));

  const updateTextDescription = (state: EditorState) => {
    setEditorState(state);
    const data = convertToRaw(state.getCurrentContent());
    const htmlData = draftToHtml(data);

    formRef.current?.setFieldValue('htmlContent',htmlData);
  };

  const editActionHandler = () => {
    setIsEditModalOpen(true);         
};

  return (
    <FormBackGround>
      <FormHeader title={title} onClose={onClose} />
      <Formik
        onSubmit={handleOnSubmit}
        initialValues={initialVal}
        validationSchema={validationSchema}
        innerRef={formRef}
      >
        {({ values }) => (
          <Form noValidate>
            <div className="d-flex justify-content-end mb-3 me-3">
              <Chip label={values.isActive ? "Active" : "Inactive"} />
            </div>
            <div className="mx-3">
              <TextField
                label="From Name"
                type="text"
                name="fromName"
              />
              <TextField
                label="From Email"
                type="text"
                name="fromEmailAddress"
              />
              <TextField
                label="Subject"
                type="text"
                name="subject"
              />
              <TextField
                label="Template Name"
                type="text"
                name="templateName"
              />
              <TextField
                label="Template Description"
                type="text"
                name="templateDescription"
              />
            </div>

            <Label
              style={generalStyles.sectionHeader}
              className="border-bottom my-3 pb-2 w-100"
            >
              Email Attributes
            </Label>
            <div className="mx-3">
              <TextField
                label="Max Attempts"
                type="number"
                name="maxAttempts"
              />
              <TextField
                label="Lock(in seconds)"
                type="number"
                name="lockLifespanSeconds"
              />
              <TextField
                label="Request(in seconds)"
                type="number"
                name="requestLifespanSeconds"
              />
              <TextField
                label="Start Period"
                name="periodStart"
                type="datetime"
              />
              <TextField
                label="End Period"
                name="periodEnd"
                type="datetime"
              />
              <TextField
                label="Max Process Time"
                name="maxProcessTime"
                type="text"
              />
              <TextField
                label="Min Process Time"
                name="minProcessTime"
                type="text"
              />
            </div>

            <Label
              style={generalStyles.sectionHeader}
              className="border-bottom my-3 pb-2 w-100"
            >
              Editor <EditButton editActionHandler={()=>editActionHandler()}/>
            </Label>
            
            <UpdateDialog
              open={isEditModalOpen}
              fullscreen
              dialogTitle="Edit HTML Content"
              saveText="Finish Editing HTML Content"
              onClose={()=>setIsEditModalOpen(false)}
              onConfirm={()=>setIsEditModalOpen(false)}
              children={
                <div className="mx-3">
                  <Editor
                    editorState={editorState}
                    onEditorStateChange={updateTextDescription}
                    toolbarClassName="toolbar-class"
                    wrapperClassName="wrapper-class"
                    editorClassName="editor-class"
                  />
                </div>
                }
            />

            <div className="mt-5">
              <ActionBar
                submitHandler={saveInput}
                cancelHandler={onClose}
                submitText="Submit"
                cancelText="Cancel"
                isLoading={status === "pending"}
              ></ActionBar>
            </div>            
          </Form>
        )}
      </Formik>
    </FormBackGround>
  );
};

export default TemplateEmailForm;
